import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  RefreshCw,
  Search,
  Shield,
  X,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useListClaimsQuery, useTransitionStageMutation } from '@/services/api/miniTortService';

import { MiniTortStageBadge } from './MiniTortStageBadge';
import { STAGE_CONFIG } from './stage-config';
import type { MiniTortClaim, MiniTortClaimListParams } from './types';
import { MiniTortStage, TERMINAL_STAGES } from './types';
import { extractApiError, formatCurrency, formatDuration, formatShortDate, hoursInStage, humanizePrereqStatus, isSatisfied } from './utils';

const PAGE_SIZE = 50;

const ALL_STAGES = Object.values(MiniTortStage);

/** Thresholds for time-in-stage urgency (in hours) */
const URGENCY_HOURS = { green: 24, yellow: 72 } as const;

/** Get urgency CSS class based on hours. green < 1d, yellow 1-3d, red > 3d */
function durationUrgencyClass(hours: number, isTerminal: boolean): string {
  if (isTerminal) return 'text-zinc-500 dark:text-zinc-400';
  if (hours > URGENCY_HOURS.yellow) return 'text-red-600 font-semibold dark:text-red-400';
  if (hours > URGENCY_HOURS.green) return 'text-amber-600 font-medium dark:text-amber-400';
  return 'text-emerald-600 dark:text-emerald-400';
}


export const MiniTortDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filter state from URL query params (bookmarkable/shareable)
  const stageFilter = searchParams.get('stage') ?? undefined;
  const injuryFilter = searchParams.get('injury') === 'true' ? true : undefined;
  const ordering = searchParams.get('sort') ?? '-created_at';
  const offset = Number(searchParams.get('offset') ?? 0);
  const reviewMode = searchParams.get('review') === '1';
  const [searchText, setSearchText] = useState(searchParams.get('q') ?? '');

  /** Merge new params into URL, preserving others */
  const updateParams = useCallback((updates: Record<string, string | undefined>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === undefined || v === '') {
          next.delete(k);
        } else {
          next.set(k, v);
        }
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setOffset = useCallback((newOffset: number) => {
    updateParams({ offset: newOffset > 0 ? String(newOffset) : undefined });
  }, [updateParams]);

  const queryParams: MiniTortClaimListParams = useMemo(
    () => ({
      stage: reviewMode ? MiniTortStage.REVIEWING_CLAIM : stageFilter,
      injury_detected: injuryFilter,
      ordering: reviewMode ? 'stage_entered_at' : ordering, // oldest first for reviews
      limit: PAGE_SIZE,
      offset,
    }),
    [stageFilter, injuryFilter, ordering, offset, reviewMode],
  );

  const { data, isLoading, isFetching, error: listError, refetch } = useListClaimsQuery(queryParams, {
    pollingInterval: 60_000,
  });
  const totalCount = data?.count ?? 0;

  // Server-side counts for summary cards — one lightweight query per stage group
  // Each returns {count} with limit=1 so minimal data transfer. RTK Query caches these.
  const { data: totalData } = useListClaimsQuery({ limit: 1 }, { pollingInterval: 60_000 });
  const { data: intakeData } = useListClaimsQuery({ stage: MiniTortStage.REVIEWING_CLAIM, limit: 1 }, { pollingInterval: 60_000 });
  const { data: materialsData } = useListClaimsQuery({ stage: MiniTortStage.NEED_ADDITIONAL_MATERIALS, limit: 1 }, { pollingInterval: 60_000 });
  const { data: demandData } = useListClaimsQuery({ stage: MiniTortStage.DEMAND_SUBMITTED, limit: 1 }, { pollingInterval: 60_000 });
  const { data: paymentData } = useListClaimsQuery({ stage: MiniTortStage.PAYMENT_ISSUED, limit: 1 }, { pollingInterval: 60_000 });
  const { data: closedData } = useListClaimsQuery({ stage: MiniTortStage.CLOSED, limit: 1 }, { pollingInterval: 60_000 });

  const serverCounts = useMemo(() => ({
    total: totalData?.count ?? 0,
    intake: intakeData?.count ?? 0,
    materials: materialsData?.count ?? 0,
    demand: demandData?.count ?? 0,
    payment: paymentData?.count ?? 0,
    closed: closedData?.count ?? 0,
  }), [totalData, intakeData, materialsData, demandData, paymentData, closedData]);

  const filteredClaims = useMemo(() => {
    const claims = data?.results ?? [];
    if (!searchText.trim()) return claims;
    const q = searchText.toLowerCase();
    return claims.filter((c) => c.matter_id.toLowerCase().includes(q) || c.id.toLowerCase().includes(q) || c.claimant_display_name.toLowerCase().includes(q));
  }, [data?.results, searchText]);

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleSort = useCallback((field: string) => {
    let newSort: string;
    if (ordering === field) {
      newSort = `-${field}`;
    } else if (ordering === `-${field}`) {
      newSort = field;
    } else {
      newSort = `-${field}`;
    }
    updateParams({ sort: newSort === '-created_at' ? undefined : newSort, offset: undefined });
  }, [ordering, updateParams]);

  const handleStageFilter = useCallback((stage: string | undefined) => {
    updateParams({ stage, review: undefined, offset: undefined });
  }, [updateParams]);

  const handleInjuryFilter = useCallback(() => {
    updateParams({ injury: injuryFilter ? undefined : 'true', offset: undefined });
  }, [injuryFilter, updateParams]);

  const toggleReviewMode = useCallback(() => {
    updateParams({ review: reviewMode ? undefined : '1', stage: undefined, offset: undefined });
  }, [reviewMode, updateParams]);

  const clearFilters = useCallback(() => {
    setSearchText('');
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const hasActiveFilters = stageFilter || injuryFilter !== undefined || searchText || reviewMode;

  // Keyboard navigation for blitz review mode
  const [focusedRow, setFocusedRow] = useState(0);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!reviewMode) return;
    setFocusedRow(0);
  }, [reviewMode, data?.results]);

  useEffect(() => {
    if (!reviewMode) return undefined;

    const handler = (e: KeyboardEvent) => {
      // Don't capture when typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        setFocusedRow((prev) => Math.min(prev + 1, filteredClaims.length - 1));
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        setFocusedRow((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        // Ctrl+Enter to approve the focused row (requires modifier to prevent accidental approvals)
        const row = tableRef.current?.querySelectorAll('tbody tr')[focusedRow];
        const approveBtn = row?.querySelector<HTMLButtonElement>('[data-action="approve"]');
        if (approveBtn) {
          e.preventDefault();
          approveBtn.click();
        }
      } else if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
        // Plain Enter navigates to claim detail
        const claims = filteredClaims;
        if (claims[focusedRow]) {
          e.preventDefault();
          navigate(`/mini-tort/claims/${claims[focusedRow].id}`);
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [reviewMode, focusedRow, filteredClaims, navigate]);

  return (
    <div className="flex flex-col gap-4 px-4 py-6 h-full sm:gap-5 sm:px-6 lg:px-10 lg:py-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <Shield className="h-6 w-6 shrink-0 text-indigo-600 sm:h-7 sm:w-7" />
            <h1 className="truncate text-2xl font-semibold text-zinc-900 dark:text-white sm:text-3xl">
              Mini-Tort Claims
            </h1>
            {totalCount > 0 && (
              <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-sm font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                {totalCount}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            Michigan property-damage reimbursement pipeline
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
        >
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Summary row: server-side counts for each stage group */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <SummaryCard label="All Claims" value={serverCounts.total} active={!stageFilter && !reviewMode} onClick={() => handleStageFilter(undefined)} />
        <SummaryCard label="In Review" value={serverCounts.intake} borderClass="border-l-indigo-500" active={stageFilter === MiniTortStage.REVIEWING_CLAIM} onClick={() => handleStageFilter(MiniTortStage.REVIEWING_CLAIM)} />
        <SummaryCard label="Needs Action" value={serverCounts.materials} borderClass="border-l-orange-500" active={stageFilter === MiniTortStage.NEED_ADDITIONAL_MATERIALS} onClick={() => handleStageFilter(MiniTortStage.NEED_ADDITIONAL_MATERIALS)} />
        <SummaryCard label="Demand" value={serverCounts.demand} borderClass="border-l-amber-500" active={stageFilter === MiniTortStage.DEMAND_SUBMITTED} onClick={() => handleStageFilter(MiniTortStage.DEMAND_SUBMITTED)} />
        <SummaryCard label="Payment" value={serverCounts.payment} borderClass="border-l-emerald-500" active={stageFilter === MiniTortStage.PAYMENT_ISSUED} onClick={() => handleStageFilter(MiniTortStage.PAYMENT_ISSUED)} />
        <SummaryCard label="Closed" value={serverCounts.closed} borderClass="border-l-zinc-400" active={stageFilter === MiniTortStage.CLOSED} onClick={() => handleStageFilter(MiniTortStage.CLOSED)} />
      </div>

      {/* Compact filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Blitz review mode toggle */}
        <button
          type="button"
          onClick={toggleReviewMode}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition-colors',
            reviewMode
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50',
          )}
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          Needs Review
        </button>

        <div className="mx-1 h-5 w-px bg-zinc-200 dark:bg-zinc-700" />

        <div className="flex flex-wrap items-center gap-1">
          <FilterPill active={!stageFilter && !reviewMode} onClick={() => handleStageFilter(undefined)} label="All" />
          {ALL_STAGES.map((stage) => (
            <FilterPill
              key={stage}
              active={!reviewMode && stageFilter === stage}
              onClick={() => handleStageFilter(stageFilter === stage ? undefined : stage)}
              label={STAGE_CONFIG[stage].shortLabel}
            />
          ))}
        </div>

        <div className="mx-1 h-5 w-px bg-zinc-200 dark:bg-zinc-700" />

        <button
          type="button"
          onClick={handleInjuryFilter}
          className={cn(
            'flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            injuryFilter === true
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
              : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700',
          )}
        >
          <AlertTriangle className="h-3 w-3" />
          Injury
        </button>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        )}

        <div className="ml-auto relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by ID..."
            className="h-7 w-48 rounded-md border border-zinc-200 bg-white pl-7 pr-3 text-xs text-zinc-700 placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:placeholder:text-zinc-500"
          />
        </div>
      </div>

      {/* Review mode banner */}
      {reviewMode && (
        <div className="flex items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5 dark:border-indigo-800 dark:bg-indigo-900/20">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" />
          <div>
            <p className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
              Review Mode — Approve or reject claims inline. Oldest first.
            </p>
            <p className="text-xs text-indigo-600 dark:text-indigo-400">
              <kbd className="rounded border border-indigo-300 bg-indigo-100 px-1 py-0.5 text-[10px] font-mono dark:border-indigo-700 dark:bg-indigo-800">↑↓</kbd> navigate
              {' '}<kbd className="rounded border border-indigo-300 bg-indigo-100 px-1 py-0.5 text-[10px] font-mono dark:border-indigo-700 dark:bg-indigo-800">Enter</kbd> open
              {' '}<kbd className="rounded border border-indigo-300 bg-indigo-100 px-1 py-0.5 text-[10px] font-mono dark:border-indigo-700 dark:bg-indigo-800">Ctrl+Enter</kbd> approve
            </p>
          </div>
        </div>
      )}

      {/* Table */}
      <div ref={tableRef} className="flex-1 overflow-hidden rounded-lg border border-zinc-200 shadow-sm dark:border-zinc-800">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
              <tr>
                <th className="w-[44px] px-3 py-2.5 font-medium text-zinc-500 dark:text-zinc-400" />
                <th className="px-3 py-2.5 font-medium text-zinc-500 dark:text-zinc-400">Claimant</th>
                {!reviewMode && <th className="px-3 py-2.5 font-medium text-zinc-500 dark:text-zinc-400">Stage</th>}
                <SortableHeader label="Time in Stage" field="stage_entered_at" ordering={ordering} onSort={handleSort} align="right" />
                <th className="px-3 py-2.5 font-medium text-zinc-500 dark:text-zinc-400">Accident</th>
                <th className="px-3 py-2.5 text-right font-medium text-zinc-500 dark:text-zinc-400">Damage</th>
                <th className="px-3 py-2.5 font-medium text-zinc-500 dark:text-zinc-400">
                  <span title="Review | 1P Coverage | 3P Coverage | Police Report">Prerequisites</span>
                </th>
                {reviewMode
                  ? <th className="px-3 py-2.5 font-medium text-zinc-500 dark:text-zinc-400">Actions</th>
                  : <th className="px-3 py-2.5 font-medium text-zinc-500 dark:text-zinc-400">Created</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {isLoading && !listError && (
                <SkeletonRows count={8} cols={reviewMode ? 7 : 8} />
              )}
              {listError && (
                <tr>
                  <td colSpan={reviewMode ? 7 : 8} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <XCircle className="h-10 w-10 text-red-400" />
                      <p className="text-sm font-medium text-red-700 dark:text-red-300">
                        Failed to load claims
                      </p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400">
                        {'status' in listError ? `Error ${listError.status}` : 'Network error'}
                      </p>
                      <button
                        type="button"
                        onClick={() => refetch()}
                        className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {!isLoading && !listError && filteredClaims.length === 0 && (
                <tr>
                  <td colSpan={reviewMode ? 7 : 8} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Shield className="h-10 w-10 text-zinc-300 dark:text-zinc-600" />
                      <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                        {reviewMode ? 'No claims waiting for review' : 'No claims found'}
                      </p>
                      {hasActiveFilters && !reviewMode && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                        >
                          Clear filters
                        </button>
                      )}
                      {reviewMode && (
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">
                          All claims in the review queue have been processed.
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {!isLoading && !listError &&
                filteredClaims.map((claim, idx) => (
                  <ClaimRow key={claim.id} claim={claim} reviewMode={reviewMode} focused={reviewMode && idx === focusedRow} onClick={() => navigate(`/mini-tort/claims/${claim.id}`)} />
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-4 py-2.5 dark:border-zinc-700 dark:bg-zinc-800/50">
            <span className="text-xs text-zinc-500 dark:text-zinc-400">
              {offset + 1}-{Math.min(offset + PAGE_SIZE, totalCount)} of {totalCount}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="flex items-center gap-1 rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-700"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev
              </button>
              <span className="text-xs text-zinc-500">{currentPage}/{totalPages}</span>
              <button
                type="button"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= totalCount}
                className="flex items-center gap-1 rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-700"
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

const SummaryCard: React.FC<{
  label: string;
  value: number | string;
  borderClass?: string;
  active?: boolean;
  alert?: boolean;
  onClick?: () => void;
}> = ({ label, value, borderClass, active, alert, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'rounded-md border border-l-4 bg-white px-3 py-2 text-left shadow-sm transition-all hover:shadow dark:bg-zinc-900',
      borderClass ?? 'border-l-zinc-300',
      active ? 'ring-2 ring-indigo-500/30 border-indigo-400' : 'border-zinc-200 dark:border-zinc-800',
      alert && 'bg-red-50 dark:bg-red-900/10',
    )}
  >
    <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</p>
    <p className={cn('mt-0.5 text-lg font-bold', alert ? 'text-red-600 dark:text-red-400' : 'text-zinc-900 dark:text-white')}>
      {value}
    </p>
  </button>
);

const FilterPill: React.FC<{ active: boolean; onClick: () => void; label: string }> = ({
  active,
  onClick,
  label,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'rounded px-2.5 py-1 text-[11px] font-medium transition-colors',
      active
        ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700',
    )}
  >
    {label}
  </button>
);

const SortableHeader: React.FC<{
  label: string;
  field: string;
  ordering: string;
  onSort: (field: string) => void;
  align?: 'left' | 'right';
}> = ({ label, field, ordering, onSort, align = 'left' }) => {
  const isAsc = ordering === field;
  const isDesc = ordering === `-${field}`;
  const active = isAsc || isDesc;

  return (
    <th className={cn('px-3 py-2.5', align === 'right' && 'text-right')}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={cn(
          'flex items-center gap-1 font-medium',
          active ? 'text-zinc-900 dark:text-white' : 'text-zinc-500 dark:text-zinc-400',
          align === 'right' && 'ml-auto',
        )}
      >
        {label}
        {isAsc && <ArrowUp className="h-3 w-3" />}
        {isDesc && <ArrowDown className="h-3 w-3" />}
        {!active && <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );
};

const ClaimRow: React.FC<{ claim: MiniTortClaim; reviewMode: boolean; focused?: boolean; onClick: () => void }> = ({ claim, reviewMode, focused, onClick }) => {
  const hours = hoursInStage(claim.stage_entered_at);
  const isTerminal = TERMINAL_STAGES.has(claim.stage);
  const isCritical = hours > URGENCY_HOURS.yellow && !isTerminal;

  return (
    <tr
      onClick={onClick}
      className={cn(
        'cursor-pointer transition-colors',
        isCritical
          ? 'bg-red-50/50 hover:bg-red-50 dark:bg-red-900/5 dark:hover:bg-red-900/10'
          : 'bg-white hover:bg-zinc-50 dark:bg-zinc-900 dark:hover:bg-zinc-800/50',
        focused && 'ring-2 ring-inset ring-indigo-500/40',
      )}
    >
      {/* Alert indicator */}
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-1">
          {claim.injury_detected && (
            <span title="Injury detected">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            </span>
          )}
          {isCritical && !claim.injury_detected && (
            <span title={`${formatDuration(hours)} in stage`}>
              <Clock className="h-4 w-4 text-red-500" />
            </span>
          )}
        </div>
      </td>

      {/* Claimant name + claim ID */}
      <td className="px-3 py-2.5">
        <Link
          to={`/mini-tort/claims/${claim.id}`}
          className="font-medium text-zinc-900 hover:text-indigo-700 dark:text-zinc-100 dark:hover:text-indigo-300"
          onClick={(e) => e.stopPropagation()}
        >
          {claim.claimant_display_name || 'Unknown'}
        </Link>
        <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
          {claim.id.slice(0, 8)}
        </p>
      </td>

      {/* Stage (hidden in review mode since all are REVIEWING_CLAIM) */}
      {!reviewMode && (
        <td className="px-3 py-2.5">
          <MiniTortStageBadge stage={claim.stage} size="sm" />
        </td>
      )}

      {/* Time in stage — right-aligned */}
      <td className="px-3 py-2.5 text-right tabular-nums">
        <span className={cn('text-sm', durationUrgencyClass(hours, isTerminal))}>
          {formatDuration(hours)}
        </span>
      </td>

      {/* Accident date */}
      <td className="px-3 py-2.5 text-xs text-zinc-600 dark:text-zinc-400">
        {formatShortDate(claim.accident_date)}
      </td>

      {/* Damage — right-aligned with tabular numbers */}
      <td className="px-3 py-2.5 text-right text-sm tabular-nums text-zinc-700 dark:text-zinc-300">
        {formatCurrency(claim.damage_amount)}
      </td>

      {/* Prerequisites: expanded in review mode, compact otherwise */}
      <td className="px-3 py-2.5">
        {reviewMode ? (
          <div className="flex items-center gap-1">
            <PrereqChipLabeled label="Rev" status={claim.review_status} />
            <PrereqChipLabeled label="1P" status={claim.coverage_1p_status} />
            <PrereqChipLabeled label="3P" status={claim.coverage_3p_status} />
            <PrereqChipLabeled label="PR" status={claim.police_report_status} />
          </div>
        ) : (
          <div className="flex items-center gap-0.5" title="Rev | 1P | 3P | PR">
            <PrereqChip label="Rev" status={claim.review_status} />
            <PrereqChip label="1P" status={claim.coverage_1p_status} />
            <PrereqChip label="3P" status={claim.coverage_3p_status} />
            <PrereqChip label="PR" status={claim.police_report_status} />
          </div>
        )}
      </td>

      {/* Review actions OR Created date */}
      {reviewMode ? (
        <td className="px-3 py-2.5">
          <InlineReviewActions claimId={claim.id} />
        </td>
      ) : (
        <td className="px-3 py-2.5 text-xs text-zinc-500 dark:text-zinc-400">
          {formatShortDate(claim.created_at)}
        </td>
      )}
    </tr>
  );
};

/** Tiny colored chip for prerequisite status */
const PrereqChip: React.FC<{ label: string; status: string }> = ({ label, status }) => {
  const satisfied = isSatisfied(status);
  const failed = status === 'failed';

  let bg: string;
  let icon: React.ReactNode = null;
  if (satisfied) {
    bg = 'bg-emerald-500';
    icon = <Check className="h-2.5 w-2.5 text-white" />;
  } else if (failed) {
    bg = 'bg-red-500';
    icon = <X className="h-2.5 w-2.5 text-white" />;
  } else {
    bg = 'bg-zinc-300 dark:bg-zinc-600';
  }

  return (
    <span
      title={`${label}: ${status}`}
      className={cn('inline-flex h-5 w-5 items-center justify-center rounded-sm text-[8px] font-bold', bg)}
    >
      {icon ?? <span className="text-white">{label[0]}</span>}
    </span>
  );
};

/** Labeled prereq chip for review mode — shows label text alongside human-friendly status */
const PrereqChipLabeled: React.FC<{ label: string; status: string }> = ({ label, status }) => {
  const satisfied = isSatisfied(status);
  const failed = status === 'failed';

  return (
    <span
      title={`${label}: ${humanizePrereqStatus(status)}`}
      className={cn(
        'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase',
        satisfied && 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
        failed && 'bg-red-50 text-red-700 dark:bg-red-900/50 dark:text-red-300',
        !satisfied && !failed && 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400',
      )}
    >
      {satisfied && <Check className="h-2.5 w-2.5" />}
      {failed && <X className="h-2.5 w-2.5" />}
      {label}
    </span>
  );
};

/** Loading skeleton rows */
const SkeletonRows: React.FC<{ count: number; cols: number }> = ({ count, cols }) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <tr key={i} className="animate-pulse bg-white dark:bg-zinc-900">
        {Array.from({ length: cols }, (__, j) => (
          <td key={j} className="px-3 py-3">
            <div className={cn('h-4 rounded bg-zinc-200 dark:bg-zinc-700', j === 0 ? 'w-6' : 'w-full')} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/** How long the undo window lasts in seconds */
const UNDO_SECONDS = 5;

/** Inline approve/reject buttons for blitz review mode with undo-able approval */
const InlineReviewActions: React.FC<{ claimId: string }> = ({ claimId }) => {
  const [transitionStage, { isLoading }] = useTransitionStageMutation();
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [result, setResult] = useState<'approved' | 'rejected' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Undo-able approve: pending state with countdown
  const [pendingApprove, setPendingApprove] = useState(false);
  const [countdown, setCountdown] = useState(UNDO_SECONDS);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cancelPendingApprove = useCallback(() => {
    setPendingApprove(false);
    setCountdown(UNDO_SECONDS);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const commitApprove = useCallback(async () => {
    cancelPendingApprove();
    try {
      await transitionStage({
        id: claimId,
        body: { new_stage: MiniTortStage.GENERATING_DEMAND },
      }).unwrap();
      setResult('approved');
    } catch (err) {
      setError(extractApiError(err, 'Approve failed — open claim for details'));
    }
  }, [cancelPendingApprove, claimId, transitionStage]);

  const startPendingApprove = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setError(null);
    setPendingApprove(true);
    setCountdown(UNDO_SECONDS);
    let remaining = UNDO_SECONDS;
    timerRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, 1000);
  }, []);

  // Auto-commit when countdown reaches 0
  useEffect(() => {
    if (pendingApprove && countdown <= 0) {
      commitApprove();
    }
  }, [pendingApprove, countdown, commitApprove]);

  // Cleanup timer on unmount
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  if (result === 'approved') {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-emerald-600" role="status" aria-live="polite">
        <CheckCircle2 className="h-3.5 w-3.5" /> Approved
      </span>
    );
  }
  if (result === 'rejected') {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-red-600" role="status" aria-live="polite">
        <XCircle className="h-3.5 w-3.5" /> Rejected
      </span>
    );
  }

  // Undo toast state
  if (pendingApprove) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()} role="status" aria-live="polite">
        <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
          Approving in {countdown}s...
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); cancelPendingApprove(); }}
          className="rounded border border-zinc-300 px-2 py-0.5 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-100 dark:border-zinc-600 dark:text-zinc-300"
        >
          Undo
        </button>
      </div>
    );
  }

  const handleReject = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setError(null);
    if (!showRejectInput) {
      setShowRejectInput(true);
      return;
    }
    if (!rejectReason.trim()) return;
    try {
      await transitionStage({
        id: claimId,
        body: { new_stage: MiniTortStage.REJECTED, rejection_reason: rejectReason },
      }).unwrap();
      setResult('rejected');
    } catch (err) {
      setError(extractApiError(err, 'Reject failed — open claim for details'));
    }
  };

  if (showRejectInput) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={rejectReason}
            onChange={(e) => { setRejectReason(e.target.value); setError(null); }}
            placeholder="Reason..."
            aria-label="Rejection reason"
            className="h-6 w-28 rounded border border-zinc-200 px-1.5 text-[11px] text-zinc-700 focus:border-red-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
            onKeyDown={(e) => { if (e.key === 'Enter') handleReject(e); if (e.key === 'Escape') setShowRejectInput(false); }}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <button
            type="button"
            onClick={handleReject}
            disabled={isLoading || !rejectReason.trim()}
            className="rounded bg-red-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'OK'}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowRejectInput(false); setError(null); }}
            className="text-[11px] text-zinc-500 hover:text-zinc-700"
          >
            Cancel
          </button>
        </div>
        {error && <p className="text-[10px] font-medium text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-4">
        <button
          type="button"
          data-action="approve"
          onClick={startPendingApprove}
          disabled={isLoading}
          aria-label={`Approve claim ${claimId.slice(0, 8)}`}
          className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          Approve
        </button>
        <button
          type="button"
          onClick={handleReject}
          disabled={isLoading}
          aria-label={`Reject claim ${claimId.slice(0, 8)}`}
          className="inline-flex items-center gap-1 rounded border border-red-200 px-2.5 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400"
        >
          <X className="h-3 w-3" />
          Reject
        </button>
      </div>
      {error && <p className="text-[10px] font-medium text-red-600">{error}</p>}
    </div>
  );
};
